import React from 'react';
import './index.css';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import AuthPage from './pages/AuthPage';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import VisionBoard from './pages/VisionBoard';
import Habits from './pages/Habits';
import Goals from './pages/Goals';
import Calendar from './pages/Calendar';
import Journal from './pages/Journal';
import Achievements from './pages/Achievements';
import Budget from './pages/Budget';
import MentalClarity from './pages/MentalClarity';
import Membership from './pages/Membership';
import PremiumSuccess from './pages/PremiumSuccess';
import Profile from './pages/Profile';
import TaskBreakdown from './pages/TaskBreakdown';
import Brainstorm from './pages/Brainstorm';
import OnboardingChat from './components/OnboardingChat';
// Public pages (no auth required)
import Landing from './pages/Landing';
import Contact from './pages/Contact';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsConditions from './pages/TermsConditions';
import CancellationRefund from './pages/CancellationRefund';
import { Toaster } from 'sonner';

const Spinner = () => (
  <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-main)' }}>
    <div className="text-lg" style={{ color: 'var(--text-secondary)' }}>Cargando...</div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (user === false) return <Navigate to="/login" replace />;
  return children;
};

const AppRoutes = () => {
  const { loading } = useAuth();
  const location = window.location;

  // OAuth callback handler — checked before anything else
  if (location.hash?.includes('session_id=') || location.pathname === '/auth/callback') {
    return (
      <Routes>
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="*" element={<AuthCallback />} />
      </Routes>
    );
  }

  if (loading) return <Spinner />;

  return (
    <Routes>
      {/* ===== Public routes (no auth required) ===== */}
      <Route path="/" element={<Landing />} />
      <Route path="/contacto" element={<Contact />} />
      <Route path="/privacidad" element={<PrivacyPolicy />} />
      <Route path="/terminos" element={<TermsConditions />} />
      <Route path="/cancelaciones" element={<CancellationRefund />} />
      <Route path="/login" element={<AuthPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* ===== Protected app routes (require auth) ===== */}
      <Route path="/dashboard" element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>} />
      <Route path="/vision-board" element={<ProtectedRoute><Layout><VisionBoard /></Layout></ProtectedRoute>} />
      <Route path="/habits" element={<ProtectedRoute><Layout><Habits /></Layout></ProtectedRoute>} />
      <Route path="/goals" element={<ProtectedRoute><Layout><Goals /></Layout></ProtectedRoute>} />
      <Route path="/calendar" element={<ProtectedRoute><Layout><Calendar /></Layout></ProtectedRoute>} />
      <Route path="/journal" element={<ProtectedRoute><Layout><Journal /></Layout></ProtectedRoute>} />
      <Route path="/achievements" element={<ProtectedRoute><Layout><Achievements /></Layout></ProtectedRoute>} />
      <Route path="/budget" element={<ProtectedRoute><Layout><Budget /></Layout></ProtectedRoute>} />
      <Route path="/clarity" element={<ProtectedRoute><Layout><MentalClarity /></Layout></ProtectedRoute>} />
      <Route path="/membership" element={<ProtectedRoute><Layout><Membership /></Layout></ProtectedRoute>} />
      <Route path="/premium/success" element={<ProtectedRoute><Layout><PremiumSuccess /></Layout></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Layout><Profile /></Layout></ProtectedRoute>} />
      <Route path="/breakdown" element={<ProtectedRoute><Layout><TaskBreakdown /></Layout></ProtectedRoute>} />
      <Route path="/brainstorm" element={<ProtectedRoute><Layout><Brainstorm /></Layout></ProtectedRoute>} />

      {/* Catch-all → landing page (was /login before) */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
            <OnboardingChat />
          </BrowserRouter>
          <Toaster position="top-right" theme="dark" />
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
